<template>
  <a-input
    placeholder="15.342.423-6"
    :max-length="12"
    v-model:value="inputValue"
    @keyup="formatRuts"
    v-bind="filteredAttrs"
  >
    <template #suffix>
      <a-button
        :disabled="filteredAttrs.disabled"
        type="link"
        @click="clearInput"
        style="padding: 0; margin: 0; width: 20px; height: 20px; line-height: 1"
        tabindex="-1"
      >
        <DeleteOutlined class="red-icon" v-if="inputValue" />
      </a-button>
    </template>
  </a-input>
</template>

<script>
import { computed } from 'vue';
import rutHelper from '@/utils/Ruts';
import { DeleteOutlined } from '@ant-design/icons-vue';

export default {
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    }
  },
  components: {
    DeleteOutlined
  },
  emits: ['update:modelValue'],

  /**
   * Setup function for the input component.
   *
   * This function initializes the component's reactive properties and methods.
   * It sets up a computed property `inputValue` to handle the two-way binding
   * of the `modelValue` prop, emitting changes back to the parent component.
   *
   * It includes the method:
   * - `formatRuts`: Formats the input RUT value to a standard format using `rutHelper`.
   *
   * @param {Object} props - The props passed to the component.
   * @param {Function} emit - The function to emit events to the parent component.
   * @returns {Object} - Returns the computed property and methods for the component.
   */
  setup(props, { emit, attrs }) {
    // Filter out onKeyup since we handle keyup internally.
    // Prevents Vue from merging the parent's @keyup (from $attrs)
    // with our own @keyup into an array, which causes:
    //   Invalid prop: type check failed for prop "onKeyup".
    //   Expected Function, got Array.
    const filteredAttrs = computed(() => {
      const result = { ...attrs };
      delete result.onKeyup;
      return result;
    });

    const inputValue = computed({
      get: () => props.modelValue,
      set: (value) => {
        emit('update:modelValue', value);
      }
    });

    /**
     * Formats the input RUT value to a standard format using `rutHelper`.
     *
     * If the input value is not empty, it formats it using `rutHelper.formatRut` and
     * updates the `inputValue` property with the formatted value.
     */
    const formatRuts = () => {
      if (inputValue.value) {
        const formattedValue = rutHelper.formatRut(inputValue.value);
        emit('update:modelValue', formattedValue);
      }
    };

    const clearInput = () => {
      emit('update:modelValue', '');
    };

    return { inputValue, formatRuts, clearInput, filteredAttrs };
  }
};
</script>
